import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { FormikHelpers } from 'formik';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import { buildPath } from '@e-socna/utils';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { PATH_MISSION_QUOTATION_DEFAULT } from '../../../app/constants/paths.constant';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { MissionPage_OneMission_QueryDocument } from '../../Mission/hooks/useFindOneMissionQuery.hook';

import { useInitQuotationInfoMutation } from '../hooks/useInitInfoMutation.hook';
import { quotationInfoForm2ApiMapper } from '../mappers/quotation-info.form2api.mapper';
import type { QuotationInfo_Init_Result } from '../hooks/useInitInfoMutation.hook';
import type { MissionQuotationWithItsInfo } from '../types/mission-quotation-with-its-info.type';
import { MISSION_SPACE_PARAM } from '../../../app/utils/mission-space.util';
import { useMissionSpace } from '../../../hooks/useMissionSpace.hook';
import { MessageList } from '../../../components/MessageList';
import { documentChoiceForm2ApiMapper } from '../../../forms/DocumentTemplates/mappers/document-choice.form2api.mapper';

export const useInitHandler = () => {
  const { folderId, missionId } = useMissionReadyContext();
  const missionSpace = useMissionSpace();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: initQuotationInfoMutation } = useInitQuotationInfoMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onInit = useCallback(
    (handleSuccess?: () => void) =>
      (
        { info, templateId, title }: Readonly<MissionQuotationWithItsInfo>,
        { setSubmitting }: Readonly<FormikHelpers<MissionQuotationWithItsInfo>>,
      ) => {
        setIsSubmitting(true);

        initQuotationInfoMutation(
          {
            missionId,
            quotationInfo: quotationInfoForm2ApiMapper(info),
            ...documentChoiceForm2ApiMapper({ templateId }),
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an undefined title becomes the empty string too
            title: title || EMPTY_STRING,
          },
          {
            onSuccess: (_: Readonly<QuotationInfo_Init_Result>) => {
              handleSuccess?.();
            },
            onError: (error: unknown) => {
              enqueueSnackbar(
                <MessageList
                  messages={userFriendlyErrors(error as BackError)}
                />,
                {
                  variant: 'error',
                },
              );
            },
            onSettled: () => {
              setIsSubmitting(false);
              setSubmitting(false);
            },
          },
        );
      },
    [enqueueSnackbar, initQuotationInfoMutation, missionId],
  );

  const handleSuccess = useCallback(() => {
    const path = buildPath(PATH_MISSION_QUOTATION_DEFAULT, {
      folderId,
      missionId,
      [MISSION_SPACE_PARAM]: missionSpace,
    });

    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(MissionPage_OneMission_QueryDocument),
        { id: missionId },
      ],
    });

    enqueueSnackbar('Le devis a bien été initialisé.');

    void navigate(path);
  }, [
    enqueueSnackbar,
    folderId,
    missionId,
    missionSpace,
    navigate,
    queryClient,
  ]);

  return { handleSuccess, isSubmitting, onInit };
};
